import React from 'react'
import { ButtonGroup } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import {
  Contract,
  ContractTenant,
  FeatureFlag,
} from '../../../../../common/validations'
import { getValue } from './utils/getValue'
import { useSuggestions } from './utils/useSuggestions'
import { isChildTenant } from './utils/isChildTenant'
import { getTenantName } from '../../../../utils/getTenantName'
import { TerminationDisallowedReason } from '../../../../../common/types'

import FormFieldWrapper from 'components/FormField'
import { TerminateSuggestionAction } from 'views/TerminateContract/hooks/useTerminateSuggestions'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'

interface TenantSelectorProps {
  tenants: ContractTenant[]
  contract: Contract
  terminate: number[]
  activeTermination?: boolean
  handleTerminateChange: (state: number[]) => void
  featureFlags?: FeatureFlag
}

export const TenantSelector: React.FC<TenantSelectorProps> = ({
  tenants,
  contract,
  terminate,
  activeTermination,
  handleTerminateChange,
}) => {
  const { contractModification } = useModuleConfig()
  const { t } = useTranslation(['contractModification'])
  const { getSuggestions } = useSuggestions(contract)
  const isPendingTerminationDisallowedReason =
    contract.terminationDisallowedReason ===
      TerminationDisallowedReason.PENDING_MODIFICATION_REQUEST ||
    contract.terminationDisallowedReason ===
      TerminationDisallowedReason.PENDING_TERMINATION_REVIEW ||
    contract.terminationDisallowedReason ===
      TerminationDisallowedReason.PENDING_TERMINATION_TENANT_APPROVALS
  const showPendingState =
    Boolean(activeTermination) || isPendingTerminationDisallowedReason
  const disableTenantSelection =
    Boolean(contract.terminationDisallowedReason) && !showPendingState
  const tenantSelectorClassName = 'mt-2 phone:w-60 w-80'
  const adultTenantIds = tenants
    .filter((tenant) => !isChildTenant(tenant))
    .map((tenant) => tenant.id)

  return tenants.map((tenant) => {
    const suggestions = getSuggestions(tenant, terminate)
    const value = getValue(contract, tenant, terminate.includes(tenant.id))

    const handleOnChange = (suggestionId: string | undefined) => {
      if (contractModification) {
        if (suggestionId === TerminateSuggestionAction.TERMINATE) {
          handleTerminateChange([...terminate, tenant.id])
        } else if (tenant.id === contract.tenant.id) {
          handleTerminateChange(
            terminate.filter(
              (i) => i !== tenant.id && !adultTenantIds.includes(i)
            )
          )
        } else {
          handleTerminateChange(terminate.filter((i) => i !== tenant.id))
        }
        return
      }

      if (suggestionId === TerminateSuggestionAction.TERMINATE) {
        handleTerminateChange(tenants.map((x) => x.id))
      } else {
        handleTerminateChange([])
      }
    }

    const isTenantPartEndedBeforeContractEnd = (
      contractEndDate: string | null | undefined,
      tenantTerminationDate: string | null | undefined
    ) => {
      if (!contractEndDate) return true
      if (!tenantTerminationDate) return false
      return (
        DateTime.fromISO(tenantTerminationDate) <
        DateTime.fromISO(contractEndDate)
      )
    }

    return (
      <FormFieldWrapper
        key={tenant.id}
        name="tenant"
        label={getTenantName(tenant)}
        labelLocation="left"
        className="mb-4"
      >
        {tenant.tenantTerminationDate &&
        (!contract.isFixedTerm ||
          isTenantPartEndedBeforeContractEnd(
            contract.endDate,
            tenant.tenantTerminationDate
          ) ||
          contract.status === 'terminated') ? (
          <div className={tenantSelectorClassName}>
            <button
              disabled
              className="mb-2 w-full border rounded-lg border-common-gray-3 bg-common-gray-3 p-2 text-common-gray-7 cursor-not-allowed"
            >
              {`${t('contractModification:terminationDisallowedReasons.ended')} ${DateTime.fromISO(tenant.tenantTerminationDate).toFormat('dd.MM.yyyy')}`}
            </button>
          </div>
        ) : showPendingState ? (
          <div className={tenantSelectorClassName}>
            <button
              disabled
              className="mb-2 w-full border rounded-lg border-common-gray-3 bg-common-gray-3 p-2 text-common-gray-7 cursor-not-allowed"
            >
              {t('contractModification:terminationDisallowedReasons.pending')}
            </button>
          </div>
        ) : (
          <div className={tenantSelectorClassName}>
            <ButtonGroup
              className="w-full"
              onChange={handleOnChange}
              suggestions={suggestions}
              value={value}
              disabled={disableTenantSelection}
              sameSize
              data-testid="tenantSelector"
            />
          </div>
        )}
      </FormFieldWrapper>
    )
  })
}
